import React, { useState, useRef, useEffect } from "react";
import { useFetchCommunityReviews } from "./hooks/useFetchCommunityReviews";
import {
  Box,
  CircularProgress,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  IconButton,
  InputAdornment,
  TextField
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport
} from "@mui/x-data-grid";
import { NavLink, useSearchParams } from "react-router-dom";
import { BatchUpdateModal } from "./BatchUpdateModal";
import Button from "@mui/material/Button";
import { STATUS_OPTIONS } from "../../constants/communityReview";
import { COLORS } from "../../constants/colors";
import SearchIcon from "@mui/icons-material/Search";

const ExtendedLink = ({ children, to, ...props }) => {
  const [searchParams] = useSearchParams();

  return (
    <NavLink to={`${to}?${searchParams.toString()}`} {...props}>
      {children}
    </NavLink>
  );
};

const columns = [
  {
    field: "id",
    headerName: "ID",
    width: 100,
    filterable: false,
    sortable: true,
    renderCell: (params) => (
      <ExtendedLink
        to={`/community-reviews/${params.value}`}
        onClick={(event) => {
          event.stopPropagation();
        }}
        style={{
          textDecoration: "none",
          color: COLORS.COMMONS_BLUE
        }}
      >
        {params.value}
      </ExtendedLink>
    )
  },
  {
    field: "userName",
    headerName: "User",
    width: 250,
    flex: 1,
    filterable: false,
    sortable: false
  },
  {
    field: "brandName",
    headerName: "Brand",
    width: 200,
    flex: 1,
    filterable: false,
    sortable: true
  },
  {
    field: "transactionId",
    headerName: "Transaction ID",
    width: 200,
    flex: 1,
    filterable: false,
    sortable: false
  },
  {
    field: "status",
    headerName: "Status",
    width: 200,
    filterable: false,
    sortable: false
  }
];

const Toolbar = ({ onBatchUpdateClick }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [brandNameInput, setBrandNameInput] = useState(
    searchParams.get("brandName") ?? ""
  );
  const [filters, setFilters] = useState({
    status: searchParams.get("status") ?? "All",
    brandName: searchParams.get("brandName") ?? ""
  });

  useEffect(() => {
    if (filters) {
      setSearchParams({
        ...Object.fromEntries(searchParams.entries()),
        ...filters
      });
    }
  }, [filters, searchParams, setSearchParams]);

  const handleBrandNameSearch = (event) => {
    event.preventDefault();
    setFilters((prev) => ({
      ...prev,
      brandName: brandNameInput
    }));
  };

  return (
    <GridToolbarContainer sx={{ mb: 1 }}>
      <FormControl sx={{ minWidth: 200 }} size="small">
        <InputLabel id="status-filter-label">Filter by Status</InputLabel>
        <Select
          labelId="status-filter-label"
          onChange={(event) =>
            setFilters((prev) => ({ ...prev, status: event.target.value }))
          }
          label="Filter by Status"
          value={filters.status || ""}
        >
          <MenuItem value="All">All</MenuItem>
          {STATUS_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 200, mr: 2 }} size="small">
        <TextField
          label="Filter by Brand"
          value={brandNameInput}
          onChange={(event) => setBrandNameInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleBrandNameSearch(event);
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleBrandNameSearch}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
          size="small"
        />
      </FormControl>
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarExport
        slotProps={{
          tooltip: { title: "Export data" },
          button: { variant: "outlined" }
        }}
      />
      <Button
        variant="outlined"
        color="primary"
        onClick={onBatchUpdateClick}
        disabled={!onBatchUpdateClick}
      >
        Batch Update
      </Button>
    </GridToolbarContainer>
  );
};

export const CommunityReviewList = ({ onRowClick }) => {
  const tableRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [paginationModel, setPaginationModel] = useState({
    page: parseInt(searchParams.get("page") ?? 0, 10),
    pageSize: parseInt(searchParams.get("pageSize") ?? 25, 10)
  });

  const { data, isLoading, refetch } = useFetchCommunityReviews({
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    status: searchParams.get("status") ?? "All",
    brandName: searchParams.get("brandName") ?? "",
    sortField: searchParams.get("sortField") ?? "id",
    sortOrder: searchParams.get("sortOrder") ?? "desc"
  });

  useEffect(() => {
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      sortField: searchParams.get("sortField") ?? "id",
      sortOrder: searchParams.get("sortOrder") ?? "desc"
    });
  }, [paginationModel, searchParams, setSearchParams]);

  useEffect(() => {
    refetch();
  }, [searchParams, refetch]);

  const onModalClose = () => {
    setIsModalOpen(false);
    setSelectedRows([]);
  };

  const handleSortModelChange = (sortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      setSearchParams((prevSearchParams) => ({
        ...prevSearchParams,
        sortField: field,
        sortOrder: sort
      }));
    }
  };

  if (isLoading) {
    return (
      <Box>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="32px">
      <DataGrid
        ref={tableRef}
        rows={data?.reviews ?? []}
        columns={columns}
        rowCount={data?.total}
        paginationModel={paginationModel}
        paginationMode="server"
        sortingMode="server"
        onPaginationModelChange={setPaginationModel}
        onSortModelChange={handleSortModelChange}
        checkboxSelection
        onRowSelectionModelChange={(newValue) => {
          setSelectedRows(newValue);
        }}
        rowSelectionModel={selectedRows}
        disableRowSelectionOnClick
        loading={isLoading}
        onRowClick={onRowClick}
        slots={{ toolbar: Toolbar }}
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "skeleton"
          },
          toolbar: {
            onBatchUpdateClick:
              selectedRows.length > 0 ? () => setIsModalOpen(true) : null
          }
        }}
      />

      <BatchUpdateModal
        reviews={selectedRows}
        isOpen={isModalOpen}
        onClose={onModalClose}
      />
    </Box>
  );
};
